import { useEffect, useState } from "react";

import Layout from "../../components/reusable/Layout";
import Title from "../../components/reusable/Title";
import CustomCard from "../../components/reusable/CustomCard";
import BiologicalAge from "./BiologicalAge/BiologicalAge";
import LineChartSection from "./BiaxialLineChart/LineChartSection";

import classes from "./BiologicalAgePage.module.css";
import { SvgConverterType } from "../../reports/Report";
import GNCSupplementTiers from "./GNCSupplementTiers";
import { ShareCardType, buildShareUrl } from "../../../share/types";
import InlineShareQR from "../../../share/components/InlineShareQR";

type LineData = {
  x: number;
  line1?: number;
  line2?: number;
  line3?: number;
  line4?: number;
  line5?: number;
  line6?: number;
  lineUser?: number;
};

type Legend2Obj = { INDEX: number; VALUE: string };

interface Props {
  data: {
    DESCRIPTION: string | null;
    INDEX: number;
    SECTION1: {
      BIOLOGICAL_AGE: string;
      BIOLOGICAL_AGE_VALUE: number;
      CHRONOLOGICAL_AGE: string;
      CHRONOLOGICAL_AGE_VALUE: number;
      COMPARED_TO: string;
      DESCRIPTION: string;
      FAT_BURNING_EFFICIENCY_VALUE: number;
      FAT_BURN_EFFICIENCY: string;
      INCREASES_YOUR_AGE: string;
      INDEX: number;
      METABOLIC_RATE: string;
      METABOLIC_RATE_VALUE: number;
      REDUCES_YOUR_AGE: string;
      TITLE: string;
      VO2MAX: string;
      VO2MAX_VALUE: number;
    };
    SECTION2: {
      DESCRIPTION: string;
      INDEX: number;
      LEGEND_1: string;
      LEGEND_2: Legend2Obj[];
      LEGEND_X_1: string;
      LEGEND_Y_1: string;
      LEGEND_Y_2: string;
      TITLE: string;
    };
    SERVICES: any[];
    TITLE: string;
  };
  onSvgConverter: SvgConverterType;
  biaxialLineChartData: {
    gender: string;
    lines_men: LineData[];
    lines_women: LineData[];
  };
  dataNew: any;
  showVO2MaxChart: boolean;
  isEvityReport?: boolean;
  isDripBarReport?: boolean;
  isCreatingPDF?: boolean;
  customShare?: string | null;
  isGNCReport?: boolean;
}

const BiologicalAgePage = ({
  data,
  onSvgConverter,
  biaxialLineChartData,
  dataNew,
  showVO2MaxChart,
  isEvityReport,
  isDripBarReport,
  isCreatingPDF,
  customShare,
  isGNCReport,
}: Props): JSX.Element => {
  const [imgBiologicalLoad, setImgBiologicalLoad] = useState(false);
  const [imgLineChartLoad, setImgLineChartLoad] = useState(false);
  useEffect(() => {
    onSvgConverter("biologicalAge", "imgBiological", 726);
    setImgBiologicalLoad(true);
    if (showVO2MaxChart) {
      onSvgConverter("lineChart", "imgLineChart", 755);
      setImgLineChartLoad(true);
    }
  }, [onSvgConverter]);

  const imgBiologicalAge = (
    <div id="biologicalAge" className={classes.imgBiologicalAge}>
      <CustomCard cardWidth="760px">
        <BiologicalAge data={data.SECTION1} />
      </CustomCard>
    </div>
  );

  const imgLineChart = (
    <LineChartSection
      id="lineChart"
      data={data.SECTION2}
      biaxialLineChartData={biaxialLineChartData}
      dataNew={dataNew}
    />
  );

  const shareUrl = buildShareUrl({
    type: ShareCardType.BIO_AGE,
    bioAge: data.SECTION1.BIOLOGICAL_AGE_VALUE,
    chronologicalAge: data.SECTION1.CHRONOLOGICAL_AGE_VALUE,
    customShare: customShare,
  });

  return (
    <Layout isEvityReport={isEvityReport}>
      <div className={classes.inlineShareQR}>
        <InlineShareQR shareUrl={shareUrl} />
      </div>
      <Title title={data.TITLE} />
      <div className={classes.section1}>
        <div id="imgBiological" className={classes.imgBiologicalAge}>
          {!imgBiologicalLoad && imgBiologicalAge}
        </div>
      </div>
      <div className={classes.description}>{data.SECTION1.DESCRIPTION}</div>
      {showVO2MaxChart && (
        <div className={classes.section2}>
          <div id="imgLineChart" className={classes.imgLineChart}>
            {!imgLineChartLoad && imgLineChart}
          </div>
          <div className={classes.description}>{data.SECTION2.DESCRIPTION}</div>
          {isDripBarReport && (
            <div className={classes.description} style={{ marginTop: "6px" }}>
              This report is based on health and wellness testing and is
              intended for informational purposes and not for the diagnosis of
              any disease. Treatment recommendations included are determined
              solely by <span className={classes.textBold}>The DRIPBaR</span>{" "}
              using their professional judgment.
            </div>
          )}
        </div>
      )}
      {isGNCReport && <GNCSupplementTiers />}
    </Layout>
  );
};

export default BiologicalAgePage;
